import React, { Fragment, useState, useEffect } from "react";
import { useParams } from "react-router";
import { getValueByKey, getCompImgUrl, displayDateByLanguage, displayDateTimeByLanguage, displayFlagByCountryCode, displayBool } from "../../Utils/utils";

import { connect } from "react-redux";
import { Grid, Box, Paper, Typography, Button, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";

import "./Composer.css";

import SideBar from "../Sidebar/SideBar";
import SheetsPagePreviewCard from "../SheetsPage/SheetsPagePreviewCard";
import MdlSidebarOverlay from "../Sidebar/MdlSidebarOverlay";
import MdlComposerEdit from "./MdlComposerEdit";

function Composer({
  userData,
}) {
  const { safeComposerName } = useParams();

  // 1. Reiner lokaler State (Kein Redux-Store-Bezug für Datenflüsse)
  const [composer, setComposer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [imgUrl, setImgUrl] = useState(undefined);
  const [uploaderName, setUploaderName] = useState("");
  const [updaterName, setUpdaterName] = useState("");
  const [composerOptions, setComposerOptions] = useState([]);
  const userLang = userData?.language || 'de';

  // Admin-Optionen (Sowohl artisttype als auch gender) parallel laden und zusammenmischen
  useEffect(() => {
    if (!userLang) return;

    // Definition der parallelen Requests
    const fetchArtistTypes = axios.get(`/settings/options?type=artisttype&lang=${userLang}`);
    const fetchGenders     = axios.get(`/settings/options?type=gender&lang=${userLang}`);

    // Promise.all feuert beide Abfragen zeitgleich ab (schneller!)
    Promise.all([fetchArtistTypes, fetchGenders])
      .then(([artistRes, genderRes]) => {
        const artistData = artistRes.data || [];
        const genderData = genderRes.data || [];
        // 👍 Hier werden beide Arrays zu einem einzigen flachen Array zusammengemischt
        const combinedOptions = [...artistData, ...genderData];
        setComposerOptions(combinedOptions);
      })
      .catch(err => {
        console.error("Fehler beim parallelen Laden der Dropdown-Optionen:", err);
      });

  }, [userLang]);

  // 2. Direktes Laden des Composers und seiner Sheets aus der DB
  useEffect(() => {
    if (!safeComposerName) return;
    setLoading(true);
    // Schritt A: Stammdaten holen
    axios.get(`/composer/${safeComposerName}`)
      .then(res => {
        if (res.data) {
          const composerData = res.data;

          // 💡 KORREKTUR Schritt B: Nutzt nun deine exakte Route mit Pfad-Parameter
          axios.get(`/search/sheetsbycomposer/${safeComposerName}`)
            .then(sheetRes => {
              // Prüft, ob die Daten direkt im Array liegen oder in einem Unterobjekt
              const sheetsList = Array.isArray(sheetRes.data) 
                ? sheetRes.data 
                : (sheetRes.data?.sheets || []);

              setComposer({
                ...composerData,
                sheets: sheetsList // Hängt die Blätter-Liste direkt an das Objekt an
              });
              setLoading(false);
            })
            .catch(err => {
              console.error("Fehler beim Laden der Blätter über die Such-Route:", err);
              // Fallback: Stammdaten ohne Blätter anzeigen
              setComposer({ ...composerData, sheets: [] });
              setLoading(false);
            });
        }
      })
      .catch(err => {
        console.error("Fehler beim direkten Datenbank-Abruf des Composers:", err);
        setLoading(false);
      });
  }, [safeComposerName]);

  // Porträt-Url und Seitentitel synchronisieren
  useEffect(() => {
    if (!loading && composer) {
      setImgUrl(getCompImgUrl(composer.portraitUrl));
      document.title = `SheetAble - ${composer.name}`;
    } else {
      document.title = "SheetAble - Lade Composer...";
    }
  }, [loading, composer]); 
  
  // Uploader-Name nachladen
  const currentUploaderId = composer ? composer.uploaderId : null;
  useEffect(() => {
    if (!currentUploaderId) return;
    axios.get(`/users/info/${currentUploaderId}`)
      .then((res) => {
        if (res.data) {
          const firstName = res.data.first_name || res.data.firstName || "";
          const lastName = res.data.last_name || res.data.lastName || "";
          const username = res.data.username || res.data.userName || "";
          const fullName = `${firstName} ${lastName}`.trim();
          setUploaderName(fullName || username || `User #${currentUploaderId}`);
        }
      })
      .catch(() => setUploaderName(`User #${currentUploaderId}`));
  }, [currentUploaderId]); 
  
  // Updater-Name nachladen
  const currentUpdaterId = composer ? composer.updaterId : null;
  useEffect(() => {
    if (!currentUpdaterId) return;
    axios.get(`/users/info/${currentUpdaterId}`)
      .then((res) => {
        if (res.data) {
          const firstName = res.data.first_name || res.data.firstName || "";
          const lastName = res.data.last_name || res.data.lastName || "";
          const username = res.data.username || res.data.userName || "";
          const fullName = `${firstName} ${lastName}`.trim();
          setUpdaterName(fullName || username || `User #${currentUpdaterId}`);
        }
      })
      .catch(() => setUpdaterName(`User #${currentUpdaterId}`));
  }, [currentUpdaterId]); 

  // 3. Der Ladewächter: Blockiert das Zeichnen unvollständiger Variablen
  if (loading || !composer) {
    return (
      <Fragment>
        <SideBar />
        <div style={{ marginLeft: "240px", padding: "48px", textAlign: "center" }}>
          <CircularProgress color="primary" style={{ marginBottom: "16px" }} />
          <Typography variant="h6" style={{ color: "#1d1b31" }}>
            Daten werden direkt aus der Datenbank geladen...
          </Typography>
        </div>
      </Fragment>
    );
  }

  // Ab hier ist GARANTIERT, dass composer existiert und voll befüllt ist -> Kein Absturz möglich!
  const musicBrainzUrl = composer.artistId ? "https://musicbrainz.org/artist/" + composer.artistId : "#";

  return (
    <Fragment>
      <SideBar />
      <div 
        style={{ 
          // 💡 KORREKTUR: Berechnet die exakte Breite ohne Padding-Konflikt
          width: "calc(100% - 240px)", 
          marginLeft: "240px",         
          display: "flex", 
          flexDirection: "column", 
          gap: "32px",
          padding: "24px",
          boxSizing: "border-box",
          height: "100vh",            // Zwingt den Container exakt auf die Fensterhöhe
          overflowY: "scroll",        // Erzwingt die vertikale Scrollbar (wird aktiv sobald nötig)
          overflowX: "hidden"         // Verbietet die horizontale Scrollbar im Keim!
        }}      >
        {/* BLOCK 1: Header (Sauber und ohne unschöne Fragezeichen) */}
        <div className="doc_header">
            <Box display="flex" justifyContent="space-between" textAlign="left" alignItems="center" width="100%">
          <Typography variant="h6" style={{ width: "100%", fontWeight: "bold", color: "#1d1b31", marginBottom: "5px" }}>
                <span className="doc_sheet">{composer.name}</span>
                <br />
                <span className="doc_composer">{composer.disambiguation}</span>
          </Typography>
            </Box>
        </div>  
      
        {/* BLOCK 2: Metadaten-Kabinett */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "32px", width: "100%" }}>
          <Paper 
            elevation={3} 
            style={{ 
              width: "100%", 
              maxWidth: "590px", 
              padding: "25px", 
              borderRadius: "15px",
              backgroundColor: "white",
              textAlign: "left"
            }}
          >
			<Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: "15px" }} >
              <Typography variant="h6" style={{ fontWeight: "bold", color: "#1d1b31", marginBottom: "5px" }}>
                Metadaten & Verlauf
              </Typography>
			<Button variant="contained" size="small" startIcon={<EditIcon />} onClick={() => setModal(true)}
			  style={{ backgroundColor: "#4f40b3", color: "white", textTransform: "none", borderRadius: "8px" }}
			>
			  Bearbeiten
			</Button>
			<MdlSidebarOverlay title="Edit Artist Details" onClose={() => setModal(false)} show={modal}>
			  <MdlComposerEdit onClose={() => setModal(false)} composer={composer} />
			</MdlSidebarOverlay>
            </Box>
            <Box display="flex" flexDirection="column" gap="4px">
              <Grid container spacing={2} style={{ padding: "2px 0", marginBottom: "1px" }}>
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Hinzugefügt am
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {displayDateTimeByLanguage(composer.createdAt, userLang)}
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Letzte Änderung
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {displayDateTimeByLanguage(composer.updatedAt, userLang)}
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Hochgeladen von
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {uploaderName || (composer.uploaderId ? "Lade Name..." : "-")}
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Geändert von
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {updaterName || (composer.updaterId ? "Lade Name..." : "-")}
                    </Box>
                  </Box>
                </Grid>  
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Gender
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {composer.gender ? getValueByKey(composerOptions, composer.gender, userLang, 'gender') : "-"}
                    </Box>
                  </Box>
                </Grid>  
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Art des Interpreten
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {/* 💡 KORREKTUR: Dynamische Key-Value Übersetzung integriert */}
                      {composer.type ? getValueByKey(composerOptions, composer.type, userLang, 'artisttype') : "-"}
                    </Box>
                  </Box>
                </Grid>  
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Herkunftsland
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {composer.countryCode ? (
                        <span>{displayFlagByCountryCode(composer.countryCode)} {composer.countryCode}</span>
                      ) : "-"}
                    </Box>
                  </Box>
                </Grid>  
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Wirkungsort
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {composer.area || "-"}
                    </Box>
                  </Box>
                </Grid>  
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Beginn der Karriere
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {composer.lifespanBegin ? displayDateByLanguage(composer.lifespanBegin) : "-"}
                    </Box>
                  </Box>
                </Grid> 
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Ende der Karriere
                    </Typography>
					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {composer.lifespanEnd ? displayDateByLanguage(composer.lifespanEnd) : "-"}
                    </Box>
                  </Box>
                </Grid> 
                
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Karriere beendet
                    </Typography>
   					<Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {displayBool(composer.lifespanIsEnded || false)}
                    </Box>
                  </Box>
                </Grid> 
                <Grid item xs={1} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#4f40b3", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      MusicBrainz-ID
                    </Typography>
                    {/* 👍 KORREKTUR: "Typography" wurde durch "Box component="div"" ersetzt, um die HTML-Verschachtelungsregeln einzuhalten */}
                    <Box component="div" style={{ fontWeight: 500, color: "#1d1b31", marginTop: "2px" }}>
                      {composer.artistId ? (
                        <a
                          href={musicBrainzUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            backgroundColor: "#f4f3f9",
                            color: "#4f40b3",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontFamily: "monospace",
                            textDecoration: "none",
                            fontWeight: 600,
                            border: "1px solid rgba(79, 64, 179, 0.15)"
                          }}
                        >
                          🆔 {composer.artistId.substring(0, 8)}... (In Enzyklopädie öffnen)
                        </a>
                      ) : (
                        "-"
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* RECHTE SPALTE: Porträt-Vorschau */}
          <Paper
            elevation={3}
            style={{
              width: "100%",
              padding: "25px",
              borderRadius: "15px",
              backgroundColor: "white",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px"
            }}
          >
            {imgUrl ? (
              <img
                src={imgUrl}
                alt={composer.name}
                style={{
                  width: "100%",
                  maxHeight: "250px",
                  objectFit: "cover",
                  borderRadius: "10px",
                  border: "1px solid rgba(0, 0, 0, 0.08)"
                }}
              />
            ) : (
              <Box
                style={{
                  width: "100%",
                  height: "200px",
                  backgroundColor: "#f4f3f9",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#4f40b3",
                  fontWeight: 500
                }}
              >
                Kein Bild vorhanden
              </Box>
            )}
          </Paper>
		  
        {/* BLOCK 4: Die Notenblätter-Liste am Ende (Sauber ohne unschöne Ternaries) */}
        <ul className="all-sheets-container full-height" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {composer.sheets.length > 0 ? (
            composer.sheets.map((sheet) => (
              <SheetsPagePreviewCard sheet={sheet} key={sheet.sheetName || sheet.id} />
            ))
          ) : (
            <Typography variant="body2" style={{ fontStyle: "italic", padding: "15px", color: "gray", textAlign: "left" }}>
              Keine Notenblätter für diesen Komponisten in der Datenbank hinterlegt.
            </Typography>
          )}
        </ul>
		  
		  
        </div>
      </div>
    </Fragment>
  );
}




// Redux-Anbindung komplett verschlankt (Nur noch für User-Metadaten wie Sprache)
const mapStateToProps = (state) => ({
  userData: state.user.credentials,
});

export default connect(mapStateToProps)(Composer);
